import { Context, Reducer, useContext, useState } from 'react';
import qs from 'qs';
import { AxiosRequestConfig } from 'axios';
import {
  IAction,
  IAPIErrorFormat,
  IModule,
  IModuleStateConfiguration,
  IStateConfiguration,
  IStoreContextValue,
  RequestStatus,
} from './moduleTypes';

import { createActions, getStateKeyFromQuery } from './common';
import { isEmpty } from './utils';
import { useApiWithToken } from './api';

type PrimaryKey = number | string;
type ChosenQuery = Record<string, unknown> | null | undefined;

export interface ILocalResponseState {
  status: number;
  data?: any;
  error?: any;
}

export interface IResponseState<T> {
  status: RequestStatus;
  list?: T;
  create?: T;
  update?: T;
  delete?: T;
  httpErrors?: IAPIErrorFormat;
}

export interface IModuleHook<T> {
  method: Function;
  response: ILocalResponseState | null | undefined;
  data: T | T[] | null | undefined;
  status: number | null | undefined;
  error?: any;
}

export default function createModule<T>(
  name: string,
  configuration: IModuleStateConfiguration,
  initialState: IResponseState<T>,
  store: Context<IStoreContextValue>
): IModule {
  // URL
  const url = configuration.url || `/${name}`;

  // Actions
  const {
    GET_ALL_REQUEST_LOADING,
    GET_ALL_REQUEST_LOADED,
    GET_ALL_REQUEST_FAILED,
    GET_BY_ID_REQUEST_LOADING,
    GET_BY_ID_REQUEST_LOADED,
    GET_BY_ID_REQUEST_FAILED,
    CREATE_REQUEST_LOADING,
    CREATE_REQUEST_LOADED,
    CREATE_REQUEST_FAILED,
    UPDATE_REQUEST_LOADING,
    UPDATE_REQUEST_LOADED,
    UPDATE_REQUEST_FAILED,
    DELETE_REQUEST_LOADING,
    DELETE_REQUEST_LOADED,
    DELETE_REQUEST_FAILED,
    CUSTOM_REQUEST_LOADING,
    CUSTOM_REQUEST_LOADED,
    CUSTOM_REQUEST_FAILED,
    HTTP_ERRORS_DISMISSED,
    STATE_RESET,
    getAllRequestLoading,
    getAllRequestLoaded,
    getAllRequestFailed,
    getByIdRequestLoading,
    getByIdRequestLoaded,
    getByIdRequestFailed,
    createRequestLoading,
    createRequestLoaded,
    createRequestFailed,
    updateRequestLoading,
    updateRequestLoaded,
    updateRequestFailed,
    deleteRequestLoading,
    deleteRequestLoaded,
    deleteRequestFailed,
    customRequestLoading,
    customRequestLoaded,
    customRequestFailed,
    httpErrorsDismissed,
    stateReset,
  } = createActions(name);

  // Slice reducers
  const reducer: Reducer<IResponseState<T>, IAction> = (
    state = initialState,
    { type, payload, query }
  ) => {
    switch (type) {
      case GET_ALL_REQUEST_LOADING:
      case GET_BY_ID_REQUEST_LOADING:
      case CREATE_REQUEST_LOADING:
      case UPDATE_REQUEST_LOADING:
      case DELETE_REQUEST_LOADING:
      case CUSTOM_REQUEST_LOADING:
        return {
          ...state,
          status: RequestStatus.LOADING,
        };
      case GET_ALL_REQUEST_LOADED:
        return {
          ...state,
          status: RequestStatus.SUCCEEDED,
          list: payload,
          httpErrors: undefined,
        };
      case GET_BY_ID_REQUEST_LOADED:
        return {
          ...state,
          status: RequestStatus.SUCCEEDED,
          [getStateKeyFromQuery(query as Record<string, unknown>)]: payload,
          httpErrors: undefined,
        };
      case CREATE_REQUEST_LOADED:
        return {
          ...state,
          status: RequestStatus.SUCCEEDED,
          create: payload,
          httpErrors: undefined,
        };
      case UPDATE_REQUEST_LOADED:
        return {
          ...state,
          status: RequestStatus.SUCCEEDED,
          update: payload,
          httpErrors: undefined,
        };
      case DELETE_REQUEST_LOADED:
        return {
          ...state,
          status: RequestStatus.SUCCEEDED,
          delete: payload,
          httpErrors: undefined,
        };
      case CUSTOM_REQUEST_LOADED:
        return {
          ...state,
          status: RequestStatus.SUCCEEDED,
          custom: payload,
          httpErrors: undefined,
        };
      case GET_ALL_REQUEST_FAILED:
      case GET_BY_ID_REQUEST_FAILED:
      case CREATE_REQUEST_FAILED:
      case UPDATE_REQUEST_FAILED:
      case DELETE_REQUEST_FAILED:
      case CUSTOM_REQUEST_FAILED:
        return {
          ...state,
          status: RequestStatus.FAILED,
          httpErrors: payload,
        };
      case HTTP_ERRORS_DISMISSED:
        return {
          ...state,
          httpErrors: undefined,
        };
      case STATE_RESET:
        return {
          ...initialState,
        };
      default:
        const extendedReducer = configuration?.extend?.reducer as Function;
        return typeof extendedReducer === 'function'
          ? extendedReducer(state, { type, payload, query })
          : state;
    }
  };

  // API Selectors Hooks
  const useModuleState = () => {
    const state = useContext(store).state;
    return state[name] as IResponseState<T>;
  };

  const useModuleStateWithQuery = (query?: Record<string, unknown>) => {
    const state = useContext(store).state;
    return isEmpty(query)
      ? state
      : state[getStateKeyFromQuery(query as Record<string, unknown>)];
  };

  const useGetAll = (): IModuleHook<T> => {
    const { dispatch, authorization } = useContext(store);
    const apiWithIDToken = useApiWithToken(authorization as string, undefined);
    const [response, setResponse] = useState<ILocalResponseState>();

    const method = async (query?: Record<string, unknown>, options?: { signal?: AbortSignal | undefined }) => {
      const chosenQuery: ChosenQuery = !isEmpty(query) ? query : null;

      dispatch(getAllRequestLoading());
      try {
        const urlWithQuery: string = chosenQuery
          ? `${url}?${qs.stringify(chosenQuery)}`
          : url;
        const { status, data } = await apiWithIDToken.get(urlWithQuery, { signal: options?.signal, });

        // NOTE - We may want to consider making the full Axios response available, not quite sure of the implications in that.
        setResponse({ status, data });

        dispatch(getAllRequestLoaded(data as T, chosenQuery));

        return { status, data };
      } catch (error) {
        // @ts-ignore
        const apiError: IAPIErrorFormat = (error?.response?.data ||
          error) as IAPIErrorFormat;

        setResponse({
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        });

        dispatch(getAllRequestFailed(apiError));

        return {
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        };
      }
    };

    return {
      method,
      response,
      data: response?.data,
      error: response?.error,
      status: response?.status,
    };
  };

  const useGetById = (): IModuleHook<T> => {
    const { dispatch, authorization } = useContext(store);
    const apiWithIDToken = useApiWithToken(authorization as string);
    const [response, setResponse] = useState<ILocalResponseState>();

    const method = async (id: PrimaryKey) => {
      dispatch(getByIdRequestLoading());
      try {
        const urlWithQuery: string = `${url}/${id}`;
        const { status, data } = await apiWithIDToken.get(urlWithQuery);

        // NOTE - We may want to consider making the full Axios response available, not quite sure of the implications in that.
        setResponse({ status, data });

        dispatch(getByIdRequestLoaded(data as T, { id }));

        return { status, data };
      } catch (error) {
        // @ts-ignore
        const apiError: IAPIErrorFormat = (error?.response?.data ||
          error) as IAPIErrorFormat;

        setResponse({
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        });

        dispatch(getByIdRequestFailed(apiError));

        return {
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        };
      }
    };

    return {
      method,
      response,
      data: response?.data,
      error: response?.error,
      status: response?.status,
    };
  };

  const useCreate = (): IModuleHook<T> => {
    const { dispatch, authorization } = useContext(store);
    const apiWithIDToken = useApiWithToken(authorization as string);
    const [response, setResponse] = useState<ILocalResponseState>();

    const method = async (payload: T) => {
      dispatch(createRequestLoading());
      try {
        const { status, data } = await apiWithIDToken.post(
          url,
          payload,
          payload instanceof FormData
            ? {
                headers: {
                  'content-type': 'multipart/form-data',
                },
              }
            : {}
        );

        // NOTE - We may want to consider making the full Axios response available, not quite sure of the implications in that.
        setResponse({ status, data });

        dispatch(createRequestLoaded(data as T));

        return { status, data };
      } catch (error) {
        // @ts-ignore
        const apiError: IAPIErrorFormat = (error?.response?.data ||
          error) as IAPIErrorFormat;

        setResponse({
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        });

        dispatch(createRequestFailed(apiError));

        return {
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        };
      }
    };

    return {
      method,
      response,
      data: response?.data,
      error: response?.error,
      status: response?.status,
    };
  };

  const useUpdate = (): IModuleHook<T> => {
    const { dispatch, authorization } = useContext(store);
    const apiWithIDToken = useApiWithToken(authorization as string);
    const [response, setResponse] = useState<ILocalResponseState>();

    const method = async (id: PrimaryKey, payload: T) => {
      dispatch(updateRequestLoading());
      try {
        const urlWithQuery = `${url}/${id}`;
        const { status, data } = await apiWithIDToken.put(
          urlWithQuery,
          payload,
          payload instanceof FormData
            ? {
                headers: {
                  'content-type': 'multipart/form-data',
                },
              }
            : {}
        );

        // NOTE - We may want to consider making the full Axios response available, not quite sure of the implications in that.
        setResponse({ status, data });

        dispatch(updateRequestLoaded(data as T, { id }));

        return { status, data };
      } catch (error) {
        // @ts-ignore
        const apiError: IAPIErrorFormat = (error?.response?.data ||
          error) as IAPIErrorFormat;

        setResponse({
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        });

        dispatch(updateRequestFailed(apiError));

        return {
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        };
      }
    };

    return {
      method,
      response,
      data: response?.data,
      error: response?.error,
      status: response?.status,
    };
  };

  const useDelete = (): IModuleHook<T> => {
    const { dispatch, authorization } = useContext(store);
    const apiWithIDToken = useApiWithToken(authorization as string);
    const [response, setResponse] = useState<ILocalResponseState>();

    const method = async (id: PrimaryKey) => {
      dispatch(deleteRequestLoading());
      try {
        const urlWithQuery = `${url}/${id}`;
        const { status, data } = await apiWithIDToken.delete(urlWithQuery);

        // NOTE - We may want to consider making the full Axios response available, not quite sure of the implications in that.
        setResponse({ status, data });

        dispatch(deleteRequestLoaded(data as T, { id }));

        return { status, data };
      } catch (error) {
        // @ts-ignore
        const apiError: IAPIErrorFormat = (error?.response?.data ||
          error) as IAPIErrorFormat;

        setResponse({
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        });

        dispatch(deleteRequestFailed(apiError));

        return {
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        };
      }
    };

    return {
      method,
      response,
      data: response?.data,
      error: response?.error,
      status: response?.status,
    };
  };

  const useRequest = (): IModuleHook<T> => {
    const { dispatch, authorization } = useContext(store);
    const apiWithIDToken = useApiWithToken(authorization as string);
    const [response, setResponse] = useState<ILocalResponseState>();

    const method = async (config: AxiosRequestConfig) => {
      dispatch(customRequestLoading());
      try {
        const { status, data } = await apiWithIDToken.request(config);

        // NOTE - We may want to consider making the full Axios response available, not quite sure of the implications in that.
        setResponse({ status, data });

        dispatch(customRequestLoaded(data as T));

        return { status, data };
      } catch (error) {
        // @ts-ignore
        const apiError: IAPIErrorFormat = (error?.response?.data ||
          error) as IAPIErrorFormat;

        setResponse({
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        });

        dispatch(customRequestFailed(apiError));

        return {
          // @ts-ignore
          status: error?.response?.status || 500,
          error: apiError,
        };
      }
    };

    return {
      method,
      response,
      data: response?.data,
      error: response?.error,
      status: response?.status,
    };
  };

  const useDismissHTTPErrors = () => {
    const dispatch = useContext(store).dispatch;
    return () => dispatch(httpErrorsDismissed());
  };

  const useResetState = () => {
    const dispatch = useContext(store).dispatch;
    return () => dispatch(stateReset());
  };

  return {
    initialState,
    reducer,
    useModuleState,
    useModuleStateWithQuery,
    useGetAll,
    useGetById,
    useCreate,
    useUpdate,
    useDelete,
    useRequest,
    useDismissHTTPErrors,
    useResetState,
  };
}

export function createModules(
  configuration: IStateConfiguration,
  initialState: Record<string, IResponseState<any>>,
  store: Context<IStoreContextValue>
) {
  return Object.keys(configuration.modules || {}).reduce((acc, name) => {
    const moduleConfiguration: IModuleStateConfiguration =
      configuration.modules[name];
    return {
      ...acc,
      [name]: createModule(
        name,
        moduleConfiguration,
        initialState[name],
        store
      ),
    };
  }, {});
}
